import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { billingService, CreditPackage } from '@/services/billingService';
import { useBalance } from '@/hooks/useBalance';
import { ChatHeader } from '@/components/ChatHeader';
import { NavigationDrawer } from '@/components/NavigationDrawer';
import { CouponRedemptionInput } from '@/components/CouponRedemptionInput';
import { useOTPAuth } from '@/hooks/useOTPAuth';

export const ManagePlanPage: React.FC = () => {
  const navigate = useNavigate();
  const { balance, isLowBalance, isZeroBalance, balanceData, isLoading: balanceLoading, refreshBalance } = useBalance();
  const { isAuthenticated, isAnonymous, user, logout } = useOTPAuth();
  const [packages, setPackages] = useState<CreditPackage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const [isDrawerPinned, setIsDrawerPinned] = useState<boolean>(() => {
    const saved = localStorage.getItem('navDrawerPinned');
    return saved === 'true';
  });
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const handleStorageChange = () => {
      const saved = localStorage.getItem('navDrawerPinned');
      setIsDrawerPinned(saved === 'true');
    };

    window.addEventListener('storage', handleStorageChange);
    const interval = setInterval(handleStorageChange, 500);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const creditPackages = await billingService.getCreditPackages();
      setPackages(creditPackages);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load credit packages';
      setError(errorMessage);
      console.error('Error fetching credit packages:', err);
    }
  };

  const handlePurchase = async (priceId: string) => {
    setLoading(true);
    setError(null);

    try {
      const response = await billingService.createCheckoutSession({
        price_id: priceId,
        success_url: `${window.location.origin}/`,
        cancel_url: `${window.location.origin}/manage-plan`,
      });

      // Redirect to Stripe Checkout
      window.location.href = response.checkout_url;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create checkout session';
      setError(errorMessage);
      console.error('Error creating checkout session:', err);
    } finally {
      setLoading(false);
    }
  };

  const showDrawer = isAuthenticated && !isAnonymous;
  const leftMargin = !isMobile && showDrawer ? (isDrawerPinned ? 'md:ml-64' : 'md:ml-16') : '';

  return (
    <div className="flex h-screen bg-neutral-white dark:bg-neutral-black">
      {showDrawer && (
        <NavigationDrawer
          isAuthenticated={isAuthenticated}
          isAnonymous={isAnonymous}
          isMobileOpen={isMobileDrawerOpen}
          onMobileClose={() => setIsMobileDrawerOpen(false)}
          user={user}
          onNewChat={() => navigate('/')}
          onLoadSession={(sessionId) => navigate('/', { state: { loadSessionId: sessionId } })}
          currentSessionId={null}
          onLogout={logout}
          balance={balance}
          isLowBalance={isLowBalance}
          isZeroBalance={isZeroBalance}
        />
      )}

      <div className={`flex flex-col flex-1 ${leftMargin} transition-all duration-300`}>
        <ChatHeader
          onMenuClick={() => setIsMobileDrawerOpen(true)}
          showMenuButton={showDrawer && isMobile}
        />

        <div className="flex-1 overflow-y-auto">
          <div className="max-w-6xl mx-auto p-6">
            {/* Balance Summary */}
            <div className="bg-white dark:bg-neutral-black border border-neutral-light-gray dark:border-neutral-dark-gray rounded-lg shadow p-6 mb-6">
              <h2 className="text-xl font-semibold text-neutral-black dark:text-neutral-white mb-4">
                Account Balance
              </h2>
              {balanceLoading ? (
                <div className="text-4xl font-bold text-neutral-medium-gray mb-2">Loading...</div>
              ) : (
                <>
                  <div className="text-4xl font-bold text-green-600 mb-2">
                    ${balanceData?.balance_usd.toFixed(2) || '0.00'}
                  </div>
                  <div className="text-sm text-neutral-medium-gray dark:text-neutral-medium-gray">
                    Lifetime spent: ${balanceData?.total_spent_usd.toFixed(2) || '0.00'} |
                    Lifetime purchased: ${balanceData?.total_purchased_usd.toFixed(2) || '0.00'}
                  </div>
                </>
              )}
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
                {error}
              </div>
            )}

            {/* Coupon Redemption */}
            <div className="mb-6">
              <CouponRedemptionInput onRedemptionSuccess={refreshBalance} />
            </div>

            {/* Credit Packages */}
            <h2 className="text-2xl font-semibold text-neutral-black dark:text-neutral-white mb-4">
              Purchase Credits
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {packages.map((pkg) => (
                <div
                  key={pkg.id}
                  className="bg-white dark:bg-neutral-black border border-neutral-light-gray dark:border-neutral-dark-gray rounded-lg shadow p-6 flex flex-col hover:shadow-lg transition-shadow"
                >
                  <h3 className="text-lg font-semibold text-neutral-black dark:text-neutral-white mb-2">
                    {pkg.name}
                  </h3>
                  <div className="text-3xl font-bold text-brand-teal mb-2">
                    ${pkg.credit_amount_usd.toFixed(0)}
                  </div>
                  <p className="text-sm text-neutral-medium-gray dark:text-neutral-medium-gray mb-4 flex-grow">
                    {pkg.description}
                  </p>
                  <button
                    onClick={() => handlePurchase(pkg.stripe_price_id)}
                    disabled={loading}
                    className="w-full px-4 py-2 bg-brand-teal hover:bg-brand-teal-hover text-white rounded-md font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Processing...' : 'Purchase'}
                  </button>
                </div>
              ))}
            </div>

            {/* Back to Chat Button */}
            <div className="text-center">
              <button
                onClick={() => navigate('/')}
                className="px-6 py-2 bg-neutral-light-gray dark:bg-neutral-dark-gray text-neutral-black dark:text-neutral-white rounded-md hover:bg-neutral-medium-gray dark:hover:bg-neutral-medium-gray transition-colors"
              >
                Back to Chat
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
